import { useCallback, useEffect, useState } from 'react';

import { useCreateUserPaymentMethod } from 'core/Api';
import { CardTokenizationFaqBadge, LearnMoreCardAccordion } from 'core/Components';
import { TokenEx } from 'core/Hooks';
import { Validator } from 'core/Utils';
import {
  Alert,
  Anchor,
  Box,
  ButtonMantine,
  Container,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  Title,
} from 'ui';
import { hasLength, isNotEmpty } from 'ui/Form';

import useGtmServices from '../../../../hooks/useGtmServices';
import './CardInfo.scss';

type Props = {
  onComplete: () => void;
  withTitle?: boolean;
  withBadge?: boolean;
  withFooterDisclaimer?: boolean;
};

const withCvv = false; // for now this static member is ideal

const AddCardTitle = () => {
  return (
    // did not match default modal styles for title
    <Stack gap="xs" mb="10">
      <Title order={2} style={(theme) => ({ color: theme?.colors.gray[8] })}>
        In-store & Dining Program
      </Title>
      <Text fz="md" fw={500} c="gray">
        Add your{' '}
        <Anchor
          href="https://support.givebacks.com/hc/en-us/articles/15283251400343-What-cards-are-NOT-eligible-for-Shop-to-Give-"
          target="_blank"
        >
          eligible
        </Anchor>{' '}
        card so you can earn donations automatically at over 10,000 restaurants and retailers!
      </Text>
    </Stack>
  );
};

const AddCardNew = ({
  onComplete,
  withTitle = true,
  withBadge = false,
  withFooterDisclaimer = true,
}: Props) => {
  const GtmService = useGtmServices();
  const [isLoading, setIsLoading] = useState(false);
  const [isLearMoreOpen, setIsLearnMoreOpen] = useState(false);
  const { mutate: createPaymentMethod } = useCreateUserPaymentMethod();
  const [error, setErrors] = useState<undefined | string>();

  const form = TokenEx.useTokenExForm({
    initialValues: {
      name: '',
      terms: false,
      month: '',
      year: '',
      zip: '',
    },
    validate: {
      name: hasLength({ min: 2 }),
      terms: isNotEmpty(),
      month: (value) => (/^(0[1-9])$|^(1[0-2])$/.test(value) ? null : true),
      year: hasLength({ min: 4 }),
      zip: (value) => (String(value).trim().length >= 5 ? null : true),
      card: isNotEmpty(''),
    },
    validateInputOnBlur: true,
  });

  const { isConfigAndTokenReady, initiateTokenEx, isCardComplete, card } = TokenEx.useTokenization({
    useRemoteConfig: true,
    useCvv: withCvv,
    onAutoFill(autoFillValues) {
      const { nameOnCard: name, month, year } = TokenEx.formatAutoFill(autoFillValues);
      form.setValues({
        name,
        month,
        year,
      });
    },
  });

  // We can add this fn as a callback to our form submit fn
  const handleAddCard = useCallback(() => {
    const { month, year, name, zip, card } = form.values;
    const isYearMonthValid = Validator.expirationDate({ month, year });
    if (form.isValid() && isYearMonthValid.isValid) {
      if (card) {
        const credential = {
          token: card.token,
          hmac: card.tokenHMAC,
          metadata: {
            card_type: card.cardType,
            last_four: card.lastFour,
            full_name: name,
            month: month,
            year: year,
            zip: zip,
          },
        };
        setIsLoading(true);
        createPaymentMethod(credential, {
          onSuccess() {
            GtmService.events.cardAdded();
            onComplete?.();
            // notifications.show({
            //   variant: 'success',
            //   title: 'Card Connected!',
            //   message:
            //     'You can now use this card for cash back offers to earn money for you and your cause. Remember to process debit card transactions WITHOUT the PIN for the most success.',
            // });
          },
          onError(e: Error) {
            const error = e as { response?: { data?: { error?: string } } };
            setErrors(error?.response?.data?.error ?? 'Unknown error occurred, please try again.');
          },
          onSettled() {
            setIsLoading(false);
          },
        });
      }
    }
  }, [form]);

  useEffect(() => {
    if (isCardComplete && card) {
      form.setFieldValue('card', card);
    } else {
      form.setFieldValue('card', undefined);
    }
  }, [isCardComplete, card?.token]);

  useEffect(() => {
    const errorOne = Object.keys(form.errors)[0] as keyof typeof TokenEx.FormErrors;
    const { month, year } = form.values;
    const isYearMonthValid = Validator.expirationDate({ month, year });
    if (errorOne) {
      setErrors(TokenEx.FormErrors[errorOne]);
    } else if (year && month && isYearMonthValid.error) {
      setErrors(isYearMonthValid.error);
    } else {
      setErrors(undefined);
    }
  }, [form.errors]);

  useEffect(() => {
    isConfigAndTokenReady && initiateTokenEx('creditCard');
  }, [isConfigAndTokenReady]);

  return (
    <TokenEx.Provider form={form}>
      <form autoComplete="on" className="StripeCard">
        {/* <Container> */}
        <Stack gap="md" align="stretch">
          {withTitle && <AddCardTitle />}
          <Stack>
            <TokenEx.Logo />
            <TokenEx.Fields withCvv={withCvv} />
            {withBadge && <CardTokenizationFaqBadge />}
          </Stack>
          <TokenEx.AgreeToTerms />
          <Alert.Transition title={error} type="error" mounted={Boolean(error)} />
          <TokenEx.SubmitButton onClick={handleAddCard} />
        </Stack>

        {withFooterDisclaimer && (
          <Container mt={8} fluid px={0}>
            <Text c="gray" ta="center" fz="sm" fw="500" w={'100%'} mb="md">
              We’re serious about security. Learn more about our{' '}
              <Anchor
                onClick={() =>
                  GtmService.click(() => setIsLearnMoreOpen(true), {
                    type: 'button',
                    action: 'info',
                    label: 'Learn More about our commitment',
                  })
                }
                fz="sm"
                fw={500}
              >
                commitment
              </Anchor>{' '}
              to keep your data safe.{' '}
            </Text>
          </Container>
        )}

        {/* </Container> */}

        <LoadingOverlay
          visible={isLoading}
          loaderProps={{ size: 'lg', color: 'violet', variant: 'dots' }}
        />
      </form>
      <Modal
        title="Learn More about the Givebacks Promise"
        styles={{
          content: { display: 'flex', flexDirection: 'column' },
          body: { display: 'flex', flexGrow: 1 },
        }}
        opened={isLearMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
        zIndex={201}
        variant="full-screen-mobile"
      >
        <Stack justify="space-between" style={{ flexGrow: 1 }}>
          <LearnMoreCardAccordion variant="separated" />
          <Box>
            <ButtonMantine onClick={() => setIsLearnMoreOpen(false)} variant="subtle">
              Back
            </ButtonMantine>
          </Box>
        </Stack>
      </Modal>
    </TokenEx.Provider>
  );
};

interface AddCardModal extends Props {
  opened: boolean;
  onClose: () => void;
}
const AddCardModal = ({ opened, onClose, onComplete }: AddCardModal) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      variant="full-screen-mobile"
      title={<AddCardTitle />}
      styles={{
        header: { alignItems: 'flex-start', position: 'relative' },
        close: { flexShrink: 0 },
      }}
    >
      <AddCardNew onComplete={onComplete} withTitle={false} />
    </Modal>
  );
};

export { AddCardModal, AddCardNew };
